import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import Swal from 'sweetalert2';
import {
  ITypesOfErrors,
  OrderErrors,
} from 'src/app/models/Orders.model';
import { OrdersService } from 'src/app/services/orders.service';
import { OpenModalService } from 'src/app/services/shared/open-modal.service';

/**
 * ErroriComponent — sub-modale per AGGIUNGERE un nuovo errore a un
 * ordine chiuso (lanciato da `<app-storico-ordini-dettagli>` H.2 via
 * il bottone "Aggiungi errore" nella sezione errori).
 *
 * Sibling mutually-exclusive di `<app-modify-error>` (H.4, modale di
 * modifica errore esistente): entrambi sono sub-modali aperti dal
 * modale principale H.2. Stesso z-index 2100 (mai aperti insieme).
 *
 * Emette `modalClosed1` al parent dopo save success così la lista
 * errori in H.2 può ricaricarsi senza full page reload.
 *
 * NB: il nome "errori" è un legacy misnomer — questo componente è in
 * realtà il modale "Aggiungi errore" (chiama `createOrderError`), non
 * una lista. La lista degli errori è in `<app-storico-ordini-dettagli>`.
 *
 * Phase 1.5 / v2.9.0 — TS cleaned up (Chunk H.3):
 *   • dropped kendo-dialog + 3× kendo-formfield + 3× kendo-label +
 *     kendoTextBox + 2× kendo-combobox + 2× kendo-formerror + 2
 *     blocchi kendo-formfield commentati dead (18 kendo refs → 0
 *     in HTML),
 *   • dropped 7 console.log/debug ("Order is brossini" 🙄, "Data is",
 *     "Error types are", "adding error", payload dumps,
 *     "I am destroyed"),
 *   • dropped 7 dead state fields: `errorNew: Errors` (mai
 *     impostato), `opened`, `isEditable`, `errori: OrderErrors`
 *     (mai usato), `selectedErrorType`, `selectedErrorDescription`,
 *     `errorTypes` init con `[null]` (anti-pattern, ora `[]`),
 *   • dropped 5 handler dead: `clearForm` (mai chiamato),
 *     `goBack()` (router.navigate da modale, errato),
 *     `goBackFunction()` (= duplicato di `close()`), `open()`,
 *     `validateError()` (mai chiamato),
 *   • dropped `ngOnDestroy` con `console.log("I am destroyed")` +
 *     `closeErrorModal()` ridondante (parent gestisce già il close
 *     via openModalService),
 *   • dropped parametro `assignOrderToSendForm: any` di
 *     `addErrorMethod` (mai letto, usava direttamente
 *     `this.errorForm.value`),
 *   • dropped Swal customClass `'pop-your-mom'` → `'swal-storico-modal'`
 *     (civile, allineato con H.4),
 *   • switch a `inject()` + `takeUntilDestroyed`,
 *   • Validators.required aggiunti correttamente in FormGroup (legacy
 *     aveva `[required]="true"` solo in template).
 *
 * Behaviour preserved 1:1: `createOrderError()` endpoint, payload
 * `IOrderErrors` con `order_error_id: 0`, Swal success/error feedback.
 */
@Component({
  selector: 'app-errori',
  templateUrl: './errori.component.html',
  styleUrls: ['./errori.component.scss'],
  standalone: false,
})


export class ErroriComponent implements OnInit {

  // ── Injected services ─────────────────────────────────────────────
  private readonly ordersServ = inject(OrdersService);
  private readonly formBuilder = inject(FormBuilder);
  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);
  public readonly closeServ = inject(OpenModalService);

  // ── Inputs / Outputs ──────────────────────────────────────────────
  @Input() order_id!: number;
  @Output() modalClosed1 = new EventEmitter<void>();

  // ── Form ──────────────────────────────────────────────────────────
  public errorForm!: FormGroup;

  // ── Data ──────────────────────────────────────────────────────────
  public errorTypes: ITypesOfErrors[] = [];
  /** Possibili valori 1-10 per la gravità errore. */
  public readonly values: number[] = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

  // ── UI state ──────────────────────────────────────────────────────
  public saving = false;

  // ──────────────────────────────────────────────────────────────────
  // Lifecycle.
  // ──────────────────────────────────────────────────────────────────

  ngOnInit(): void {
    this.errorForm = this.formBuilder.group({
      oe_description: [
        '',
        [Validators.required, Validators.maxLength(500)],
      ],
      oe_error_gravity: ['', Validators.required],
      oe_error_type_id: ['', Validators.required],
    });
    this.loadErrorTypes();
  }

  // ──────────────────────────────────────────────────────────────────
  // Data loaders.
  // ──────────────────────────────────────────────────────────────────

  private loadErrorTypes(): void {
    this.ordersServ
      .getTypesOfOrderErrors()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data: ITypesOfErrors[]) => {
          this.errorTypes = data ?? [];
          this.cdr.detectChanges();
        },
      });
  }

  // ──────────────────────────────────────────────────────────────────
  // Submit.
  // ──────────────────────────────────────────────────────────────────

  addError(): void {
    if (!this.errorForm.valid || this.saving) return;
    this.saving = true;

    const f = this.errorForm.value;
    const assignError = new OrderErrors({
      oe_description: f.oe_description,
      oe_error_gravity: f.oe_error_gravity,
      oe_error_type_id: f.oe_error_type_id,
      order_error_id: 0,
      order_id: +this.order_id,
    });

    this.ordersServ
      .createOrderError(assignError)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (ordersData: any) => {
          this.saving = false;
          if (ordersData?.success) {
            this.modalClosed1.emit();
            this.closeServ.closeErrorModal();
            Swal.fire({
              title: 'Impostato!',
              text: 'Errore salvato con successo.',
              icon: 'success',
              customClass: { popup: 'swal-storico-modal' },
              timer: 1500,
              timerProgressBar: true,
            });
          } else {
            Swal.fire({
              title: 'Non impostato',
              text: 'Si è verificato un problema, riprova.',
              icon: 'error',
              customClass: { popup: 'swal-storico-modal' },
              timer: 1500,
              timerProgressBar: true,
            });
          }
          this.cdr.detectChanges();
        },
        error: () => {
          this.saving = false;
          Swal.fire({
            title: 'Non impostato',
            text: 'Errore di rete, riprova.',
            icon: 'error',
            customClass: { popup: 'swal-storico-modal' },
            timer: 1500,
            timerProgressBar: true,
          });
          this.cdr.detectChanges();
        },
      });
  }

  // ──────────────────────────────────────────────────────────────────
  // Close.
  // ──────────────────────────────────────────────────────────────────

  close(): void {
    this.closeServ.closeErrorModal();
  }
  
}
